import { useEffect, useRef, useState } from 'react';
import { Spin } from '@arco-design/web-react';
import type { PortalApplication, PortalIdentity } from '../model/portalModels';
import type { RuntimePrimaryNavigationItem } from '@jukun/platform-sdk/auth';
import { defaultPortalPath } from '../navigation/portalNavigation';
import { ensureAccessToken } from '../modules/auth/accessTokenManager';
import {
  getRegisteredNavigationSnapshot,
  invalidateRegisteredNavigationApplication,
  startRegisteredApplication,
  syncRegisteredApplicationRoute,
} from '../runtime/wujieLoader';
import { PortalStatePage } from './PortalStatePage';
import { usePortalOverlay } from './PortalOverlayProvider';

function releaseWithoutUnhandled(cleanup?: () => void | Promise<void>) {
  if (!cleanup) return;
  void Promise.resolve().then(() => cleanup()).catch(() => undefined);
}

export function MicroAppHost(props: {
  application: PortalApplication;
  identity: PortalIdentity;
  pathname: string;
  mountId?: string;
  switchAttemptId?: string;
  navigate: (path: string, mountId?: string, switchAttemptId?: string) => void;
  onPrimaryNavigationRegister: (
    items: readonly RuntimePrimaryNavigationItem[],
    runtimeRegistration?: boolean,
    mountId?: string,
    switchAttemptId?: string,
  ) => void;
  onPrimaryNavigationChange: (
    navigationCode: string,
    mountId?: string,
    switchAttemptId?: string,
  ) => void;
  onSessionExpired: () => void;
  onLoadFailed?: (mountId: string, switchAttemptId?: string) => void;
  onLoadReady?: (mountId: string, switchAttemptId?: string) => void;
  onLoadStageChange?: (
    stage: 'access-token' | 'runtime-start' | 'navigation-register',
    mountId: string,
    switchAttemptId?: string,
  ) => void;
}) {
  const { reportOperationFeedback } = usePortalOverlay();
  const containerRef = useRef<HTMLDivElement>(null);
  const mountedPathnameRef = useRef(props.pathname);
  const activeMountIdRef = useRef<string | undefined>(undefined);
  const [state, setState] = useState<'loading' | 'mounted' | 'failed'>(() =>
    getRegisteredNavigationSnapshot(props.application.appCode).length
      ? 'mounted' : 'loading');
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const controller = new AbortController();
    const mountId = props.mountId ?? crypto.randomUUID();
    let release: (() => void | Promise<void>) | undefined;
    activeMountIdRef.current = mountId;
    const isCurrentMount = () => activeMountIdRef.current === mountId
      && !controller.signal.aborted;
    const restoring = getRegisteredNavigationSnapshot(
      props.application.appCode,
    ).length > 0;
    setState(restoring ? 'mounted' : 'loading');
    props.onPrimaryNavigationRegister([], false, mountId, props.switchAttemptId);
    props.onLoadStageChange?.('access-token', mountId, props.switchAttemptId);
    const container = containerRef.current;
    const registerPrimaryNavigation = (
      items: readonly RuntimePrimaryNavigationItem[],
    ) => {
      if (isCurrentMount()) props.onPrimaryNavigationRegister(
        items,
        true,
        mountId,
        props.switchAttemptId,
      );
    };
    const reportPrimaryNavigationChange = (navigationCode: string) => {
      if (isCurrentMount()) props.onPrimaryNavigationChange(
        navigationCode,
        mountId,
        props.switchAttemptId,
      );
    };
    void ensureAccessToken(
      props.application.appCode,
      false,
      controller.signal,
    ).then(token => {
      if (controller.signal.aborted) throw new DOMException('aborted', 'AbortError');
      props.onLoadStageChange?.('runtime-start', mountId, props.switchAttemptId);
      return startRegisteredApplication({
        application: props.application,
        mountId,
        signal: controller.signal,
        runtimePurpose: 'navigation',
        container,
        identity: props.identity,
        pathname: props.pathname,
        accessToken: token,
        refreshAccessToken: () => ensureAccessToken(
          props.application.appCode, true, controller.signal,
        ),
        registerPrimaryNavigation,
        navigate: internalPath => {
          if (isCurrentMount()) props.navigate(
            `${props.application.entry.basePath}${internalPath}`,
            mountId,
            props.switchAttemptId,
          );
        },
        onPrimaryNavigationChange: reportPrimaryNavigationChange,
        onSessionExpired: props.onSessionExpired,
        reportOperationFeedback,
      });
    }).then(cleanup => {
      if (!isCurrentMount()) releaseWithoutUnhandled(cleanup);
      else {
        release = cleanup;
        syncRegisteredApplicationRoute(props.application, props.pathname);
        setState('mounted');
        props.onLoadStageChange?.(
          'navigation-register',
          mountId,
          props.switchAttemptId,
        );
        props.onLoadReady?.(mountId, props.switchAttemptId);
      }
    }).catch(() => {
      if (isCurrentMount()) {
        props.onPrimaryNavigationRegister([], false, mountId, props.switchAttemptId);
        setState('failed');
        props.onLoadFailed?.(mountId, props.switchAttemptId);
      }
    });
    return () => {
      controller.abort();
      if (activeMountIdRef.current === mountId) {
        activeMountIdRef.current = undefined;
        props.onPrimaryNavigationRegister([], false, mountId, props.switchAttemptId);
      }
      releaseWithoutUnhandled(release);
    };
  }, [props.application, props.identity, props.navigate, props.onPrimaryNavigationChange,
    props.mountId, props.onPrimaryNavigationRegister, props.onSessionExpired,
    props.onLoadFailed, props.onLoadReady, props.onLoadStageChange, props.switchAttemptId,
    reportOperationFeedback, revision]);

  useEffect(() => {
    if (state === 'mounted' && mountedPathnameRef.current !== props.pathname) {
      mountedPathnameRef.current = props.pathname;
      syncRegisteredApplicationRoute(props.application, props.pathname);
    }
  }, [props.application, props.pathname, state]);

  const retry = () => {
    setState('loading');
    void invalidateRegisteredNavigationApplication(props.application.appCode)
      .then(() => setRevision(value => value + 1))
      .catch(() => setState('failed'));
  };

  return <div className="portal-micro-app-host">
    {state === 'loading' ? <div className="portal-micro-app-state"><Spin loading tip="正在加载应用..." /></div> : null}
    {state === 'failed' ? <div className="portal-micro-app-state">
      <PortalStatePage variant="system-error" mode="page"
        onRetry={retry}
        onBack={() => props.navigate(
          defaultPortalPath,
          activeMountIdRef.current,
          props.switchAttemptId,
        )} />
    </div> : null}
    <div className="portal-micro-app-container" ref={containerRef}
      hidden={state !== 'mounted'} data-app-code={props.application.appCode} />
  </div>;
}
